import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import toast, { Toaster } from "react-hot-toast";
import {
  FileText,
  ScrollText,
  ShieldCheck,
  Plus,
  Edit3,
  Eye,
  Send,
  AlertCircle,
  History as HistoryIcon,
} from "lucide-react";

import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../../../components/ui/Tabs";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../../../components/ui/Table";
import Button from "../../../components/ui/Button";
import { Spinner } from "../../../components/ui/Spinner";

import {
  coerceKind,
  coerceTermsStatus,
  getTerms,
  listTerms,
  publishTerms,
  termsKindLabel,
  type PlatformTermsKind,
  type PlatformTermsView,
} from "../../../api/General/PlatformTerms";
import TermsDraftDialog from "./TermsDraftDialog";
import PublishConfirmDialog from "./PublishConfirmDialog";
import TermsPreviewDialog from "./TermsPreviewDialog";
import { getApiErrorMessage } from '../../../utils/apiErrors';

const KIND_TABS: { value: PlatformTermsKind; key: string; icon: React.ReactNode }[] = [
  { value: "GeneralUsage", key: "General Usage", icon: <FileText size={14} /> },
  { value: "TrialUpgrade", key: "Trial Upgrade", icon: <ScrollText size={14} /> },
  { value: "Privacy", key: "Privacy", icon: <ShieldCheck size={14} /> },
];

const PlatformTermsIndex: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === "rtl";

  const [activeKind, setActiveKind] = useState<PlatformTermsKind>("GeneralUsage");

  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [rows, setRows] = useState<PlatformTermsView[]>([]);

  const [draftOpen, setDraftOpen] = useState(false);
  const [draftSeed, setDraftSeed] = useState<PlatformTermsView | null>(null);
  const [previewRow, setPreviewRow] = useState<PlatformTermsView | null>(null);
  const [publishTarget, setPublishTarget] = useState<PlatformTermsView | null>(null);

  const fetchRows = useCallback(async (kind: PlatformTermsKind) => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const res = await listTerms(kind);
      if (res.errors && res.errors.length > 0) {
        setErrorMsg(getApiErrorMessage(res, t, t("Failed to load terms")));
        setRows([]);
      } else {
        const list = res.payload || [];
        // Stable order: version desc
        list.sort((a, b) => (b.version || 0) - (a.version || 0));
        setRows(list);
      }
    } catch (err) {
      setErrorMsg(getApiErrorMessage(err, t, t("Failed to load terms")));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchRows(activeKind);
  }, [activeKind, fetchRows]);

  const latestPublished = useMemo(
    () => rows.find((r) => coerceTermsStatus(r.status) === "Published"),
    [rows],
  );

  const handleNewDraft = () => {
    // Seed from latest published version when available
    if (latestPublished) {
      setDraftSeed({ ...latestPublished, id: 0, status: "Draft", version: 0 });
    } else {
      setDraftSeed({
        id: 0,
        kind: activeKind,
        version: 0,
        title: "",
        contentMarkdown: "",
        status: "Draft",
      });
    }
    setDraftOpen(true);
  };

  const handleEdit = async (row: PlatformTermsView) => {
    try {
      const res = await getTerms(row.id);
      if (res.errors && res.errors.length > 0) {
        toast.error(getApiErrorMessage(res, t, t("Failed to load terms")));
        return;
      }
      setDraftSeed(res.payload || row);
      setDraftOpen(true);
    } catch (err) {
      toast.error(getApiErrorMessage(err, t, t("Failed to load terms")));
    }
  };

  const onDraftSaved = (saved?: PlatformTermsView) => {
    setDraftOpen(false);
    setDraftSeed(null);
    toast.success(t("Draft saved"));
    fetchRows(activeKind);
    if (saved) setPreviewRow(saved);
  };

  const handlePublishClick = (row: PlatformTermsView) => setPublishTarget(row);
  const handlePublishConfirm = async () => {
    if (!publishTarget) return;
    try {
      const res = await publishTerms(publishTarget.id);
      if (res.errors && res.errors.length > 0) {
        toast.error(getApiErrorMessage(res, t, t("Failed to publish")));
        return;
      }
      toast.success(t("Published. All users will be prompted on next login."));
      setPublishTarget(null);
      fetchRows(activeKind);
    } catch (err) {
      toast.error(getApiErrorMessage(err, t, t("Failed to publish")));
    }
  };

  return (
    <div className="min-h-full bg-muted/20">
      <Toaster position={isRtl ? "top-left" : "top-right"} />

      {/* Header band */}
      <div className="border-b border-border bg-card">
        <div className="max-w-[1400px] mx-auto px-6 py-5 flex items-center gap-4">
          <div className="shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-slate-700 to-slate-900 dark:from-slate-200 dark:to-slate-400 text-white dark:text-slate-900 flex items-center justify-center shadow-sm">
            <FileText size={22} strokeWidth={2} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-semibold text-foreground tracking-tight">
                {t("Platform Terms")}
              </h1>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              {t("Author and publish the platform-wide documents users must accept.")}
            </p>
          </div>
          <Button onClick={handleNewDraft}>
            <Plus size={14} className="me-1.5" />
            {t("New draft")}
          </Button>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 py-6">
        <Tabs value={activeKind} onValueChange={(v) => setActiveKind(v as PlatformTermsKind)}>
          <TabsList className="bg-card border border-border">
            {KIND_TABS.map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value} className="gap-1.5">
                <span className="text-muted-foreground">{tab.icon}</span>
                {t(tab.key)}
              </TabsTrigger>
            ))}
          </TabsList>

          {KIND_TABS.map((tab) => (
            <TabsContent key={tab.value} value={tab.value} className="mt-4">
              <div className="rounded-xl border border-border bg-card overflow-hidden">
                <div className="px-4 py-3 border-b border-border flex items-center justify-between bg-muted/30">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <HistoryIcon size={14} />
                    {t("All versions, newest first")}
                  </div>
                  {latestPublished && activeKind === tab.value && (
                    <div className="text-xs text-muted-foreground">
                      {t("Currently live")}: v{latestPublished.version}
                    </div>
                  )}
                </div>

                <div className="relative">
                  {loading && (
                    <div className="absolute inset-0 z-10 bg-card/60 backdrop-blur-[1px] flex items-center justify-center">
                      <Spinner size="md" />
                    </div>
                  )}

                  {errorMsg && !loading && (
                    <div className="p-6">
                      <div className="flex items-start gap-2 rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 p-3 text-sm text-red-800 dark:text-red-300">
                        <AlertCircle size={16} className="mt-0.5 shrink-0" />
                        <span className="flex-1">{errorMsg}</span>
                        <Button variant="outline" size="sm" onClick={() => fetchRows(activeKind)}>
                          {t("Retry")}
                        </Button>
                      </div>
                    </div>
                  )}

                  {!errorMsg && (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[80px] ps-4">{t("Version")}</TableHead>
                          <TableHead>{t("Title")}</TableHead>
                          <TableHead className="w-[120px]">{t("Status")}</TableHead>
                          <TableHead className="w-[160px]">{t("Effective")}</TableHead>
                          <TableHead className="w-[160px]">{t("Published")}</TableHead>
                          <TableHead className="w-[200px]">{t("By")}</TableHead>
                          <TableHead className="text-end pe-4 w-[260px]">
                            <span className="sr-only">{t("Actions")}</span>
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {rows.length === 0 && !loading && (
                          <TableRow>
                            <TableCell colSpan={7} className="py-16 text-center">
                              <div className="flex flex-col items-center gap-2 text-muted-foreground">
                                <FileText size={20} className="opacity-50" />
                                <div className="text-sm">{t("No versions yet.")}</div>
                                <Button variant="outline" size="sm" onClick={handleNewDraft}>
                                  <Plus size={12} className="me-1" />
                                  {t("Create the first draft")}
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                        {rows.map((row) => {
                          const kind = coerceKind(row.kind);
                          const status = coerceTermsStatus(row.status);
                          const isDraft = status === "Draft";
                          return (
                            <TableRow key={row.id}>
                              <TableCell className="ps-4 tabular-nums font-semibold text-foreground">
                                v{row.version}
                              </TableCell>
                              <TableCell>
                                <div className="text-sm font-medium text-foreground line-clamp-1">
                                  {row.title || t(termsKindLabel(kind))}
                                </div>
                              </TableCell>
                              <TableCell>
                                <StatusBadge status={status} />
                              </TableCell>
                              <TableCell className="text-xs tabular-nums text-muted-foreground">
                                {row.effectiveFrom
                                  ? new Date(row.effectiveFrom).toLocaleDateString()
                                  : "—"}
                              </TableCell>
                              <TableCell className="text-xs tabular-nums text-muted-foreground">
                                {row.publishedAt
                                  ? new Date(row.publishedAt).toLocaleDateString()
                                  : "—"}
                              </TableCell>
                              <TableCell className="text-xs text-muted-foreground truncate">
                                {row.publishedByName || "—"}
                              </TableCell>
                              <TableCell className="text-end pe-4">
                                <div className="inline-flex items-center gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setPreviewRow(row)}
                                  >
                                    <Eye size={14} className="me-1" />
                                    {t("View")}
                                  </Button>
                                  {isDraft && (
                                    <>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleEdit(row)}
                                      >
                                        <Edit3 size={14} className="me-1" />
                                        {t("Edit")}
                                      </Button>
                                      <Button
                                        size="sm"
                                        onClick={() => handlePublishClick(row)}
                                      >
                                        <Send size={14} className="me-1" />
                                        {t("Publish")}
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>

      {/* Draft create/edit dialog */}
      <TermsDraftDialog
        open={draftOpen}
        onOpenChange={(o) => {
          if (!o) setDraftSeed(null);
          setDraftOpen(o);
        }}
        seed={draftSeed}
        defaultKind={activeKind}
        onSaved={onDraftSaved}
      />

      {/* Preview dialog */}
      <TermsPreviewDialog
        open={!!previewRow}
        onOpenChange={(o) => !o && setPreviewRow(null)}
        record={previewRow}
      />

      {/* Publish confirm */}
      <PublishConfirmDialog
        open={!!publishTarget}
        onOpenChange={(o) => !o && setPublishTarget(null)}
        record={publishTarget}
        onConfirm={handlePublishConfirm}
      />
    </div>
  );
};

/* ─────────── Status badge ─────────── */
const StatusBadge: React.FC<{ status: "Draft" | "Published" }> = ({ status }) => {
  const { t } = useTranslation();
  if (status === "Published") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300 ring-1 ring-emerald-200/60 dark:ring-emerald-900/40">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
        {t("Published")}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 ring-1 ring-amber-200/60 dark:ring-amber-900/40">
      <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
      {t("Draft")}
    </span>
  );
};

export default PlatformTermsIndex;
